import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/components/poof-ui';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { FanArtSection } from '@/components/FanArtSection';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { playBoing, playWhoosh, playPop, playHover, playClick } from '@/utils/sounds';
import {
  getManyArtistBio,
  subscribeManyArtistBio,
  ArtistBioResponse,
} from '@/lib/collections/artistBio';
import {
  getManyWipPosts,
  subscribeManyWipPosts,
  WipPostsResponse,
} from '@/lib/collections/wipPosts';
import {
  subscribeManyChatMessages,
  setChatMessages,
  ChatMessagesResponse,
} from '@/lib/collections/chatMessages';
import {
  setWipLikes,
  deleteWipLikes,
  subscribeManyWipLikes,
  WipLikesResponse,
} from '@/lib/collections/wipLikes';
import {
  setWipComments,
  subscribeManyWipComments,
  WipCommentsResponse,
} from '@/lib/collections/wipComments';
import { Time } from '@/lib/db-client';
import { useAuth } from '@pooflabs/web';
import { toast } from 'sonner';
import { ArrowLeft, Send, MessageCircle, Palette, Heart, ChevronDown, ChevronUp } from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRelativeTime(unixSeconds: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const CHAT_NAME_KEY = 'dangerdan_chat_name';

// ─── Bio Section ─────────────────────────────────────────────────────────────

const BioSection: React.FC<{ bio: ArtistBioResponse | null; loading: boolean }> = ({
  bio,
  loading,
}) => {
  const sectionRef = useScrollAnimation<HTMLElement>(0.05);
  const [showReel, setShowReel] = useState(false);

  // Lock body scroll when reel popup is open
  React.useEffect(() => {
    if (showReel) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showReel]);

  return (
    <section
      ref={sectionRef}
      className="scroll-fade-up relative py-16 px-6 flex justify-center"
      style={{ opacity: 1, transform: 'none' }}
    >
      {/* Loading state: empty container so the observer can attach */}
      {loading && null}

      {/* No bio after loading: coming soon placeholder */}
      {!loading && !bio && (
        <div
          className="relative max-w-lg w-full text-center py-12 px-8"
          style={{
            background: 'rgba(10,10,15,0.85)',
            border: '4px solid #1a1a2a',
            boxShadow: '6px 6px 0 rgba(0,0,0,0.7)',
            transform: 'rotate(-0.5deg)',
          }}
        >
          <div
            style={{
              display: 'inline-block',
              background: 'rgba(124,252,0,0.1)',
              border: '2px solid rgba(124,252,0,0.25)',
              color: 'rgba(124,252,0,0.5)',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.8rem',
              letterSpacing: '0.18em',
              padding: '2px 12px',
              marginBottom: '16px',
            }}
          >
            COMING SOON
          </div>
          <p
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              letterSpacing: '0.08em',
              color: 'rgba(255,255,255,0.35)',
            }}
          >
            Artist profile dropping soon
          </p>
        </div>
      )}

      {/* Bio card */}
      {!loading && bio && (
        <>
          {/* Dot grid bg */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(124,252,0,0.8) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
              opacity: 0.02,
            }}
          />

          <div className="relative max-w-2xl w-full">
            {/* Comic tag */}
            <div
              style={{
                display: 'inline-block',
                background: '#E63946',
                color: '#fff',
                fontFamily: "'Bangers', cursive",
                fontSize: '0.85rem',
                letterSpacing: '0.16em',
                padding: '2px 14px',
                border: '3px solid rgba(0,0,0,0.7)',
                boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
                transform: 'rotate(-1.5deg)',
                marginBottom: '20px',
                userSelect: 'none',
              }}
            >
              THE ARTIST
            </div>

            {/* Card */}
            <div
              style={{
                background: 'linear-gradient(135deg, #0a0a0f 0%, #111118 100%)',
                border: '4px solid #1a1a2a',
                boxShadow: '8px 8px 0 rgba(0,0,0,0.8), 0 0 0 1px rgba(124,252,0,0.08)',
                transform: 'rotate(-0.8deg)',
                overflow: 'hidden',
              }}
            >
              <div className="flex flex-col md:flex-row">
                {/* Image */}
                {bio.imageUrl && (
                  <div
                    className="shrink-0 md:w-56"
                    style={{
                      borderRight: '4px solid #1a1a2a',
                      background: '#080d1f',
                      position: 'relative',
                      minHeight: '200px',
                    }}
                  >
                    <img
                      src={bio.imageUrl}
                      alt={bio.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        minHeight: '200px',
                      }}
                    />
                    {/* Scan line overlay */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage:
                          'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 p-6 md:p-8">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1
                      style={{
                        fontFamily: "'Bangers', cursive",
                        fontSize: 'clamp(2rem, 6vw, 3.5rem)',
                        letterSpacing: '0.1em',
                        color: '#7CFC00',
                        textShadow: '3px 3px 0 rgba(0,0,0,0.8), 0 0 20px rgba(124,252,0,0.2)',
                        lineHeight: 1,
                        marginBottom: '0px',
                      }}
                    >
                      {bio.name}
                    </h1>
                    {bio.xAccount && (
                      <a
                        href={bio.xAccount.startsWith('@')
                          ? `https://x.com/${bio.xAccount.slice(1)}`
                          : bio.xAccount}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={playPop}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          background: 'rgba(56,189,248,0.1)',
                          border: '2px solid rgba(56,189,248,0.35)',
                          boxShadow: '2px 2px 0 rgba(0,0,0,0.6)',
                          color: '#38BDF8',
                          cursor: 'pointer',
                          transition: 'background 0.15s ease, box-shadow 0.15s ease',
                          flexShrink: 0,
                          marginBottom: '0',
                          alignSelf: 'center',
                        }}
                        onMouseEnter={e => {
                          playHover();
                          (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(56,189,248,0.2)';
                          (e.currentTarget as HTMLAnchorElement).style.boxShadow = '3px 3px 0 rgba(0,0,0,0.7), 0 0 12px rgba(56,189,248,0.25)';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(56,189,248,0.1)';
                          (e.currentTarget as HTMLAnchorElement).style.boxShadow = '2px 2px 0 rgba(0,0,0,0.6)';
                        }}
                        title={bio.xAccount}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                      </a>
                    )}
                  </div>
                  <p
                    style={{
                      fontFamily: "'Bangers', cursive",
                      fontSize: '1rem',
                      letterSpacing: '0.2em',
                      color: '#E63946',
                      marginBottom: '16px',
                      marginTop: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {bio.role}
                  </p>
                  <div
                    style={{
                      height: '2px',
                      background: 'linear-gradient(90deg, #7CFC00, transparent)',
                      marginBottom: '16px',
                      opacity: 0.4,
                    }}
                  />
                  <p
                    style={{
                      color: 'rgba(255,255,255,0.75)',
                      lineHeight: 1.7,
                      fontSize: '0.95rem',
                    }}
                  >
                    {bio.bio}
                  </p>

                  {bio.videoUrl && (
                    <button
                      onClick={() => { playWhoosh(); setShowReel(true); }}
                      className="inline-flex items-center gap-2 mt-5 px-4 py-2 text-xs font-bold uppercase transition-all duration-200"
                      style={{
                        fontFamily: "'Bangers', cursive",
                        letterSpacing: '0.12em',
                        background: 'rgba(124,252,0,0.1)',
                        border: '2px solid rgba(124,252,0,0.4)',
                        color: '#7CFC00',
                        boxShadow: '3px 3px 0 rgba(0,0,0,0.5)',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => {
                        playHover();
                        (e.currentTarget as HTMLButtonElement).style.background =
                          'rgba(124,252,0,0.2)';
                        (e.currentTarget as HTMLButtonElement).style.boxShadow =
                          '4px 4px 0 rgba(0,0,0,0.7), 0 0 15px rgba(124,252,0,0.3)';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLButtonElement).style.background =
                          'rgba(124,252,0,0.1)';
                        (e.currentTarget as HTMLButtonElement).style.boxShadow =
                          '3px 3px 0 rgba(0,0,0,0.5)';
                      }}
                    >
                      ▶ Watch Reel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Reel video popup */}
      {showReel && bio?.videoUrl && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => { playPop(); setShowReel(false); }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '700px',
              background: '#0a0a0f',
              border: '5px solid #E63946',
              boxShadow: '8px 8px 0 rgba(0,0,0,0.9), 0 0 40px rgba(230,57,70,0.2)',
              transform: 'rotate(-1deg)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* NOW PLAYING tag */}
            <div
              style={{
                position: 'absolute',
                top: '-2px',
                left: '12px',
                transform: 'translateY(-100%)',
                background: '#E63946',
                color: '#fff',
                fontFamily: "'Bangers', cursive",
                fontSize: '0.75rem',
                letterSpacing: '0.2em',
                padding: '2px 10px',
                border: '3px solid rgba(0,0,0,0.7)',
                borderBottom: 'none',
                boxShadow: '3px -3px 0 rgba(0,0,0,0.5)',
                userSelect: 'none',
              }}
            >
              NOW PLAYING
            </div>

            {/* Close button */}
            <button
              onClick={() => { playPop(); setShowReel(false); }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                zIndex: 10,
                background: 'rgba(0,0,0,0.7)',
                border: '2px solid rgba(255,255,255,0.2)',
                color: '#fff',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontFamily: "'Bangers', cursive",
                fontSize: '1.1rem',
                lineHeight: 1,
              }}
              onMouseEnter={e => {
                playHover();
                (e.currentTarget as HTMLButtonElement).style.background = '#E63946';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.7)';
              }}
              aria-label="Close video"
            >
              ✕
            </button>

            {/* Video */}
            <video
              src={bio.videoUrl}
              controls
              autoPlay
              playsInline
              style={{ width: '100%', display: 'block', background: '#000' }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

// ─── WIP Comment Item ─────────────────────────────────────────────────────────

const WipCommentItem: React.FC<{ comment: WipCommentsResponse }> = ({ comment }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-baseline gap-2">
      <span
        style={{
          fontFamily: "'Bangers', cursive",
          fontSize: '0.85rem',
          letterSpacing: '0.06em',
          color: '#7CFC00',
        }}
      >
        {comment.displayName}
      </span>
      <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)' }}>
        {formatRelativeTime(comment.createdAt)}
      </span>
    </div>
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '2px solid rgba(255,255,255,0.07)',
        padding: '7px 10px',
        borderRadius: '0 6px 6px 6px',
        color: 'rgba(255,255,255,0.82)',
        fontSize: '0.82rem',
        lineHeight: 1.5,
        wordBreak: 'break-word',
      }}
    >
      {comment.message}
    </div>
  </div>
);

// ─── WIP Card ─────────────────────────────────────────────────────────────────

const TILTS = [-1.5, 1.2, -0.8, 1.8, -1.2, 0.9, -1.8, 1.5];

interface WipCardProps {
  post: WipPostsResponse;
  index: number;
  allLikes: WipLikesResponse[];
  allComments: WipCommentsResponse[];
}

const WipCard: React.FC<WipCardProps> = ({ post, index, allLikes, allComments }) => {
  const tilt = TILTS[index % TILTS.length];
  const { user } = useAuth();
  const walletAddress = user?.address ?? null;

  // Optimistic like state: 'added' | 'removed' | null (null = follow subscription)
  const [optimisticLike, setOptimisticLike] = useState<'added' | 'removed' | null>(null);
  // Optimistic comments: appended immediately, removed once subscription confirms them
  const [optimisticComments, setOptimisticComments] = useState<WipCommentsResponse[]>([]);

  // Filter likes/comments for this post from subscription data
  const serverPostLikes = allLikes.filter(l => l.postId === post.id);
  const serverPostComments = allComments.filter(c => c.postId === post.id);

  const likeId = walletAddress ? `${walletAddress}_${post.id}` : null;
  const serverHasLiked = likeId ? serverPostLikes.some(l => l.id === likeId) : false;

  // Reconcile optimistic like: once subscription confirms the state we expected, clear the optimistic override
  React.useEffect(() => {
    if (optimisticLike === 'added' && serverHasLiked) setOptimisticLike(null);
    if (optimisticLike === 'removed' && !serverHasLiked) setOptimisticLike(null);
  }, [serverHasLiked, optimisticLike]);

  // Reconcile optimistic comments: remove ones that have been confirmed by subscription
  React.useEffect(() => {
    if (optimisticComments.length === 0) return;
    const confirmedIds = new Set(serverPostComments.map(c => c.id));
    setOptimisticComments(prev => prev.filter(c => !confirmedIds.has(c.id)));
  }, [serverPostComments]);

  // Derive displayed values with optimistic overrides applied
  const hasLiked = optimisticLike === 'added' ? true : optimisticLike === 'removed' ? false : serverHasLiked;

  // Build optimistic like entry for count purposes
  const optimisticLikeEntry: WipLikesResponse | null =
    optimisticLike === 'added' && likeId && walletAddress
      ? { id: likeId, postId: post.id, walletAddress, createdAt: Math.floor(Date.now() / 1000), tarobase_created_at: Math.floor(Date.now() / 1000) }
      : null;

  const postLikesForDisplay = React.useMemo(() => {
    if (optimisticLike === 'added' && optimisticLikeEntry) {
      // Add optimistic entry if not already present
      return serverPostLikes.some(l => l.id === likeId)
        ? serverPostLikes
        : [...serverPostLikes, optimisticLikeEntry];
    }
    if (optimisticLike === 'removed') {
      return serverPostLikes.filter(l => l.id !== likeId);
    }
    return serverPostLikes;
  }, [serverPostLikes, optimisticLike, optimisticLikeEntry, likeId]);

  // Merge optimistic comments with server comments, deduped by id, sorted newest-first
  const postComments = React.useMemo(() => {
    const confirmedIds = new Set(serverPostComments.map(c => c.id));
    const pendingComments = optimisticComments.filter(c => !confirmedIds.has(c.id));
    return [...serverPostComments, ...pendingComments].sort((a, b) => b.createdAt - a.createdAt);
  }, [serverPostComments, optimisticComments]);

  const likeCount = postLikesForDisplay.length;
  const commentCount = postComments.length;

  const [likePending, setLikePending] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentMessage, setCommentMessage] = useState('');
  const [commentName, setCommentName] = useState<string>(() => {
    try { return localStorage.getItem(CHAT_NAME_KEY) ?? ''; } catch { return ''; }
  });
  const [nameInput, setNameInput] = useState('');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [pendingComment, setPendingComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const handleLike = async () => {
    if (!walletAddress || !likeId || likePending) return;
    setLikePending(true);
    // Apply optimistic update immediately
    if (hasLiked) {
      playPop();
      setOptimisticLike('removed');
    } else {
      playBoing();
      setOptimisticLike('added');
    }
    try {
      if (hasLiked) {
        const ok = await deleteWipLikes(likeId);
        if (!ok) {
          setOptimisticLike(null);
          toast.error('Could not update like');
        }
      } else {
        const ok = await setWipLikes(likeId, {
          postId: post.id,
          walletAddress,
          createdAt: Time.Now,
        });
        if (!ok) {
          setOptimisticLike(null);
          toast.error('Could not update like');
        }
      }
    } catch {
      setOptimisticLike(null);
      toast.error('Could not update like');
    } finally {
      setLikePending(false);
    }
  };

  const doPostComment = async (name: string, message: string) => {
    if (!name.trim() || !message.trim()) return;
    setSendingComment(true);
    const id = crypto.randomUUID();
    const now = Math.floor(Date.now() / 1000);
    // Optimistically append the comment immediately
    const optimistic: WipCommentsResponse = {
      id,
      postId: post.id,
      displayName: name.trim(),
      message: message.trim(),
      walletAddress: walletAddress ?? undefined,
      createdAt: now,
      tarobase_created_at: now,
    };
    setOptimisticComments(prev => [...prev, optimistic]);
    try {
      const ok = await setWipComments(id, {
        postId: post.id,
        displayName: name.trim(),
        message: message.trim(),
        walletAddress: walletAddress ?? undefined,
        createdAt: Time.Now,
      });
      if (ok) {
        setCommentMessage('');
        playBoing();
      } else {
        setOptimisticComments(prev => prev.filter(c => c.id !== id));
        toast.error('Failed to post comment');
      }
    } catch {
      setOptimisticComments(prev => prev.filter(c => c.id !== id));
      toast.error('Failed to post comment');
    } finally {
      setSendingComment(false);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!walletAddress) return;
    if (!commentMessage.trim()) return;
    if (!commentName) {
      setPendingComment(commentMessage);
      setShowNamePrompt(true);
      return;
    }
    await doPostComment(commentName, commentMessage);
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!walletAddress) return;
    if (!nameInput.trim()) return;
    playPop();
    const name = nameInput.trim();
    try { localStorage.setItem(CHAT_NAME_KEY, name); } catch {}
    setCommentName(name);
    setShowNamePrompt(false);
    setNameInput('');
    if (pendingComment) {
      await doPostComment(name, pendingComment);
      setPendingComment('');
    }
  };

  return (
    <div
      className="wip-card"
      style={{
        background: 'linear-gradient(160deg, #0a0a0f 0%, #111118 100%)',
        border: '4px solid #1a1a2a',
        boxShadow: '5px 5px 0 rgba(0,0,0,0.7)',
        transform: `rotate(${tilt}deg)`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: 'default',
        overflow: 'hidden',
        breakInside: 'avoid',
        marginBottom: '1.5rem',
        opacity: 0,
        animationDelay: `${index * 0.1}s`,
      }}
      onMouseEnter={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.transform = `rotate(${tilt * 0.3}deg) translateY(-4px)`;
        el.style.boxShadow = '8px 8px 0 rgba(0,0,0,0.8), 0 0 20px rgba(124,252,0,0.1)';
      }}
      onMouseLeave={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.transform = `rotate(${tilt}deg)`;
        el.style.boxShadow = '5px 5px 0 rgba(0,0,0,0.7)';
      }}
    >
      {/* Media */}
      <div style={{ position: 'relative', overflow: 'hidden', background: '#050508' }}>
        {post.mediaType === 'video' ? (
          <video
            src={post.mediaUrl}
            autoPlay
            muted
            loop
            playsInline
            style={{ width: '100%', display: 'block', maxHeight: '280px', objectFit: 'cover' }}
          />
        ) : (
          <img
            src={post.mediaUrl}
            alt={post.title}
            loading="lazy"
            style={{ width: '100%', display: 'block', maxHeight: '280px', objectFit: 'cover' }}
          />
        )}
        {/* Corner tag */}
        <div
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: post.mediaType === 'video' ? '#E63946' : '#7CFC00',
            color: post.mediaType === 'video' ? '#fff' : '#0a0a0f',
            fontFamily: "'Bangers', cursive",
            fontSize: '0.65rem',
            letterSpacing: '0.15em',
            padding: '1px 7px',
            border: '2px solid rgba(0,0,0,0.6)',
            boxShadow: '2px 2px 0 rgba(0,0,0,0.5)',
          }}
        >
          {post.mediaType === 'video' ? 'VIDEO' : 'ART'}
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: '12px 14px 10px' }}>
        <h3
          style={{
            fontFamily: "'Bangers', cursive",
            fontSize: '1.15rem',
            letterSpacing: '0.06em',
            color: '#ffffff',
            textShadow: '1px 1px 0 rgba(0,0,0,0.6)',
            marginBottom: post.description ? '4px' : 0,
          }}
        >
          {post.title}
        </h3>
        {post.description && (
          <p
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.8rem',
              lineHeight: 1.5,
            }}
          >
            {post.description}
          </p>
        )}
      </div>

      {/* ── Like & Comment bar ─────────────────────────── */}
      <div
        style={{
          borderTop: '2px solid rgba(255,255,255,0.06)',
          padding: '8px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(0,0,0,0.2)',
        }}
      >
        {/* Like button */}
        <button
          onClick={handleLike}
          disabled={!walletAddress || likePending}
          title={walletAddress ? (hasLiked ? 'Unlike' : 'Like this') : 'Connect wallet to like'}
          onMouseEnter={playHover}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            background: hasLiked ? 'rgba(124,252,0,0.12)' : 'rgba(255,255,255,0.04)',
            border: hasLiked ? '2px solid rgba(124,252,0,0.5)' : '2px solid rgba(255,255,255,0.1)',
            color: hasLiked ? '#7CFC00' : 'rgba(255,255,255,0.4)',
            padding: '4px 10px',
            cursor: walletAddress && !likePending ? 'pointer' : 'default',
            fontFamily: "'Bangers', cursive",
            fontSize: '0.85rem',
            letterSpacing: '0.08em',
            transition: 'all 0.15s ease',
            boxShadow: hasLiked ? '0 0 10px rgba(124,252,0,0.2)' : 'none',
            opacity: likePending ? 0.6 : 1,
          }}
        >
          <Heart
            size={13}
            fill={hasLiked ? '#7CFC00' : 'none'}
            stroke={hasLiked ? '#7CFC00' : 'rgba(255,255,255,0.4)'}
            strokeWidth={2.5}
          />
          <span>{likeCount > 0 ? likeCount : ''}</span>
          <span style={{ fontSize: '0.75rem' }}>{hasLiked ? 'LIKED' : 'LIKE'}</span>
        </button>

        {/* Comment toggle button */}
        <button
          onClick={() => { playClick(); setCommentsOpen(v => !v); }}
          onMouseEnter={playHover}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            background: commentsOpen ? 'rgba(124,252,0,0.08)' : 'rgba(255,255,255,0.04)',
            border: commentsOpen ? '2px solid rgba(124,252,0,0.3)' : '2px solid rgba(255,255,255,0.1)',
            color: commentsOpen ? 'rgba(124,252,0,0.8)' : 'rgba(255,255,255,0.4)',
            padding: '4px 10px',
            cursor: 'pointer',
            fontFamily: "'Bangers', cursive",
            fontSize: '0.85rem',
            letterSpacing: '0.08em',
            transition: 'all 0.15s ease',
          }}
        >
          <MessageCircle size={13} strokeWidth={2.5} />
          <span>{commentCount > 0 ? commentCount : ''}</span>
          <span style={{ fontSize: '0.75rem' }}>
            {commentCount === 1 ? 'COMMENT' : 'COMMENTS'}
          </span>
          {commentsOpen
            ? <ChevronUp size={11} />
            : <ChevronDown size={11} />
          }
        </button>
      </div>

      {/* ── Comments panel (inline expand) ───────────────── */}
      {commentsOpen && (
        <div
          style={{
            background: '#080d1f',
            borderTop: '2px solid rgba(124,252,0,0.12)',
            padding: '12px 14px 14px',
            animation: 'wip-comments-slide-in 0.25s ease-out forwards',
          }}
        >
          {/* Comments list */}
          {postComments.length === 0 ? (
            <p
              style={{
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.2)',
                fontFamily: "'Bangers', cursive",
                letterSpacing: '0.1em',
                textAlign: 'center',
                padding: '8px 0 10px',
              }}
            >
              No comments yet — be first!
            </p>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                maxHeight: '220px',
                overflowY: 'auto',
                marginBottom: '12px',
                paddingRight: '4px',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(124,252,0,0.15) transparent',
              }}
            >
              {postComments.map(c => (
                <WipCommentItem key={c.id} comment={c} />
              ))}
            </div>
          )}

          {/* Divider */}
          <div
            style={{
              height: '1px',
              background: 'rgba(124,252,0,0.1)',
              marginBottom: '10px',
            }}
          />

          {/* Comment form */}
          {!walletAddress ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
                Connect wallet to comment
              </p>
            </div>
          ) : showNamePrompt ? (
            <form onSubmit={handleNameSubmit} style={{ display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1 }}>
                <input
                  autoFocus
                  type="text"
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  placeholder="Pick a display name..."
                  maxLength={24}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.06)',
                    border: '2px solid rgba(124,252,0,0.35)',
                    color: '#fff',
                    padding: '7px 10px',
                    outline: 'none',
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    boxShadow: '0 0 8px rgba(124,252,0,0.1)',
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={!nameInput.trim()}
                onMouseEnter={playHover}
                style={{
                  background: '#7CFC00',
                  color: '#0a0a0f',
                  border: '2px solid rgba(0,0,0,0.5)',
                  boxShadow: '2px 2px 0 rgba(0,0,0,0.6)',
                  padding: '7px 12px',
                  fontFamily: "'Bangers', cursive",
                  fontSize: '0.85rem',
                  letterSpacing: '0.08em',
                  cursor: nameInput.trim() ? 'pointer' : 'not-allowed',
                  opacity: nameInput.trim() ? 1 : 0.5,
                  whiteSpace: 'nowrap',
                  alignSelf: 'flex-start',
                }}
              >
                Set Name
              </button>
            </form>
          ) : (
            <form onSubmit={handleCommentSubmit} style={{ display: 'flex', gap: '6px' }}>
              <input
                type="text"
                value={commentMessage}
                onChange={e => setCommentMessage(e.target.value)}
                placeholder="Leave a comment..."
                maxLength={280}
                disabled={sendingComment}
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.05)',
                  border: '2px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  padding: '7px 10px',
                  outline: 'none',
                  fontSize: '0.8rem',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={e => {
                  (e.target as HTMLInputElement).style.borderColor = 'rgba(124,252,0,0.4)';
                }}
                onBlur={e => {
                  (e.target as HTMLInputElement).style.borderColor = 'rgba(255,255,255,0.1)';
                }}
              />
              <button
                type="submit"
                disabled={sendingComment || !commentMessage.trim()}
                onMouseEnter={e => {
                  playHover();
                  if (!sendingComment && commentMessage.trim()) {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.transform = 'none';
                }}
                style={{
                  background: sendingComment || !commentMessage.trim() ? 'rgba(124,252,0,0.2)' : '#7CFC00',
                  color: '#0a0a0f',
                  border: '2px solid rgba(0,0,0,0.4)',
                  boxShadow: '2px 2px 0 rgba(0,0,0,0.5)',
                  padding: '7px 10px',
                  cursor: sendingComment || !commentMessage.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.15s ease, transform 0.1s ease',
                  flexShrink: 0,
                }}
              >
                <Send size={14} />
              </button>
            </form>
          )}

          {/* "Commenting as" indicator */}
          {commentName && !showNamePrompt && (
            <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)' }}>
                Commenting as{' '}
                <button
                  onClick={() => {
                    playPop();
                    setCommentName('');
                    try { localStorage.removeItem(CHAT_NAME_KEY); } catch {}
                  }}
                  onMouseEnter={playHover}
                  style={{
                    color: '#7CFC00',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: "'Bangers', cursive",
                    fontSize: '0.85rem',
                    letterSpacing: '0.05em',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    padding: 0,
                  }}
                >
                  {commentName}
                </button>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── WIP Gallery ─────────────────────────────────────────────────────────────

const WipGallery: React.FC<{ posts: WipPostsResponse[] }> = ({ posts }) => {
  const sectionRef = useScrollAnimation<HTMLElement>(0.05);

  // Subscribe to all likes and comments — filter per-card to avoid N subscriptions
  const { data: allLikesData } = useRealtimeData<WipLikesResponse[]>(
    subscribeManyWipLikes,
    true,
  );
  const { data: allCommentsData } = useRealtimeData<WipCommentsResponse[]>(
    subscribeManyWipComments,
    true,
  );

  const allLikes = allLikesData ?? [];
  const allComments = allCommentsData ?? [];

  const sorted = [...posts].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <section ref={sectionRef} className="scroll-fade-up relative py-16 px-6">
      {/* Section header */}
      <div className="container mb-10 flex flex-col items-center">
        <div
          style={{
            display: 'inline-block',
            background: '#7CFC00',
            color: '#0a0a0f',
            fontFamily: "'Bangers', cursive",
            fontSize: '0.8rem',
            letterSpacing: '0.2em',
            padding: '2px 14px',
            border: '3px solid rgba(0,0,0,0.7)',
            boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
            transform: 'rotate(1.2deg)',
            marginBottom: '12px',
            userSelect: 'none',
          }}
        >
          WORK IN PROGRESS
        </div>
        <h2
          style={{
            fontFamily: "'Bangers', cursive",
            fontSize: 'clamp(2rem, 7vw, 4rem)',
            letterSpacing: '0.1em',
            color: '#ffffff',
            textShadow: '4px 4px 0 rgba(0,0,0,0.8)',
            textAlign: 'center',
          }}
        >
          THE GALLERY
        </h2>
        <div
          style={{
            height: '3px',
            width: '80px',
            background: 'linear-gradient(90deg, #E63946, #7CFC00)',
            marginTop: '8px',
          }}
        />
      </div>

      {sorted.length === 0 ? (
        <div className="container flex flex-col items-center py-16">
          <Palette size={40} style={{ color: 'rgba(255,255,255,0.1)', marginBottom: '12px' }} />
          <p
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '1.5rem',
              letterSpacing: '0.1em',
              color: 'rgba(255,255,255,0.2)',
            }}
          >
            The artist is cooking...
          </p>
          <p style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.85rem', marginTop: '6px' }}>
            Check back soon for WIP drops
          </p>
        </div>
      ) : (
        <div
          className="container wip-masonry"
        >
          {sorted.map((post, i) => (
            <WipCard
              key={post.id}
              post={post}
              index={i}
              allLikes={allLikes}
              allComments={allComments}
            />
          ))}
        </div>
      )}

      <style>{`
        .wip-masonry {
          columns: 1;
          column-gap: 1.5rem;
        }
        @media (min-width: 640px) { .wip-masonry { columns: 2; } }
        @media (min-width: 1024px) { .wip-masonry { columns: 3; } }
        .wip-card {
          animation: wip-fade-in 0.5s ease-out forwards;
        }
        @keyframes wip-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes wip-comments-slide-in {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

// ─── Chat Bubble ─────────────────────────────────────────────────────────────

const ChatBubble: React.FC<{ msg: ChatMessagesResponse; isNew: boolean }> = ({
  msg,
  isNew,
}) => (
  <div
    className="flex flex-col gap-1"
    style={{
      animation: isNew ? 'chat-pop 0.3s ease-out' : undefined,
    }}
  >
    <div className="flex items-baseline gap-2">
      <span
        style={{
          fontFamily: "'Bangers', cursive",
          fontSize: '0.9rem',
          letterSpacing: '0.06em',
          color: '#7CFC00',
        }}
      >
        {msg.displayName}
      </span>
      <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)' }}>
        {formatRelativeTime(msg.timestamp)}
      </span>
    </div>
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '2px solid rgba(255,255,255,0.07)',
        padding: '8px 12px',
        borderRadius: '0 8px 8px 8px',
        color: 'rgba(255,255,255,0.85)',
        fontSize: '0.9rem',
        lineHeight: 1.5,
        wordBreak: 'break-word',
      }}
    >
      {msg.message}
    </div>
  </div>
);

// ─── Chat Section ─────────────────────────────────────────────────────────────

const ChatSection: React.FC = () => {
  const sectionRef = useScrollAnimation<HTMLElement>(0.05);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevCountRef = useRef(0);

  const { user, login } = useAuth();
  const walletAddress = user?.address ?? null;

  const [displayName, setDisplayName] = useState<string>(() => {
    try { return localStorage.getItem(CHAT_NAME_KEY) ?? ''; } catch { return ''; }
  });
  const [nameInput, setNameInput] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  // Optimistic messages: shown immediately after send, removed once confirmed in subscription data
  const [optimisticMessages, setOptimisticMessages] = useState<ChatMessagesResponse[]>([]);

  const { data: messages } = useRealtimeData<ChatMessagesResponse[]>(
    subscribeManyChatMessages,
    true,
  );

  // Remove optimistic messages that have been confirmed by the subscription
  useEffect(() => {
    if (!messages || optimisticMessages.length === 0) return;
    const confirmedIds = new Set(messages.map(m => m.id));
    setOptimisticMessages(prev => prev.filter(m => !confirmedIds.has(m.id)));
  }, [messages]);

  // Merge subscription data with optimistic messages, deduplicated by id
  const allMessages = React.useMemo(() => {
    const base = messages ?? [];
    const confirmedIds = new Set(base.map(m => m.id));
    const pending = optimisticMessages.filter(m => !confirmedIds.has(m.id));
    return [...base, ...pending];
  }, [messages, optimisticMessages]);

  const sorted = [...allMessages].sort((a, b) => a.timestamp - b.timestamp);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const count = sorted.length;
    if (count > prevCountRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      if (count > 0) {
        const lastId = sorted[count - 1]?.id;
        if (lastId) {
          setNewIds(prev => new Set([...prev, lastId]));
          setTimeout(() => {
            setNewIds(prev => {
              const next = new Set(prev);
              next.delete(lastId);
              return next;
            });
          }, 1000);
        }
      }
    }
    prevCountRef.current = count;
  }, [sorted.length]);

  const doSend = async (name: string, message: string) => {
    if (!walletAddress) return;
    if (!name.trim() || !message.trim()) return;
    setSending(true);
    const id = crypto.randomUUID();
    const ts = Math.floor(Date.now() / 1000);
    // Add optimistic message immediately so it appears without waiting for subscription
    const optimistic: ChatMessagesResponse = {
      id,
      displayName: name.trim(),
      message: message.trim(),
      timestamp: ts,
      tarobase_created_at: ts,
    };
    setOptimisticMessages(prev => [...prev, optimistic]);
    try {
      const ok = await setChatMessages(id, {
        displayName: name.trim(),
        message: message.trim(),
        timestamp: Time.Now,
      });
      if (ok) {
        setMessageInput('');
      } else {
        // Remove optimistic message on failure
        setOptimisticMessages(prev => prev.filter(m => m.id !== id));
        toast.error('Failed to send message');
      }
    } catch {
      // Remove optimistic message on error
      setOptimisticMessages(prev => prev.filter(m => m.id !== id));
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!walletAddress) return;
    if (!messageInput.trim()) return;

    if (!displayName) {
      setPendingMessage(messageInput);
      setShowNamePrompt(true);
      return;
    }

    playBoing();
    await doSend(displayName, messageInput);
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!walletAddress) return;
    if (!nameInput.trim()) return;
    playPop();
    const name = nameInput.trim();
    try { localStorage.setItem(CHAT_NAME_KEY, name); } catch {}
    setDisplayName(name);
    setShowNamePrompt(false);
    setNameInput('');
    if (pendingMessage) {
      await doSend(name, pendingMessage);
      setPendingMessage('');
    }
  };

  return (
    <section ref={sectionRef} className="scroll-fade-up relative py-16 px-6">
      {/* Section divider */}
      <div
        style={{
          height: '3px',
          background: 'linear-gradient(90deg, transparent, #E63946 30%, #7CFC00 50%, #E63946 70%, transparent)',
          marginBottom: '48px',
          opacity: 0.4,
        }}
      />

      <div className="container max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div
            style={{
              display: 'inline-block',
              background: '#E63946',
              color: '#fff',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.8rem',
              letterSpacing: '0.18em',
              padding: '2px 12px',
              border: '3px solid rgba(0,0,0,0.7)',
              boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
              transform: 'rotate(-1deg)',
              userSelect: 'none',
            }}
          >
            LIVE
          </div>
          <h2
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
              letterSpacing: '0.1em',
              color: '#ffffff',
              textShadow: '2px 2px 0 rgba(0,0,0,0.7)',
            }}
          >
            <MessageCircle
              size={22}
              style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px', color: '#7CFC00' }}
            />
            THE CHAT
          </h2>
          {displayName && (
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.3)',
              }}
            >
              Chatting as{' '}
              <button
                onClick={() => {
                  playPop();
                  setDisplayName('');
                  try { localStorage.removeItem(CHAT_NAME_KEY); } catch {}
                }}
                onMouseEnter={playHover}
                style={{
                  color: '#7CFC00',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: "'Bangers', cursive",
                  fontSize: '0.9rem',
                  letterSpacing: '0.05em',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                }}
              >
                {displayName}
              </button>
            </span>
          )}
        </div>

        {/* Messages */}
        <div
          style={{
            background: 'rgba(5,5,12,0.85)',
            border: '4px solid #1a1a2a',
            boxShadow: '5px 5px 0 rgba(0,0,0,0.8)',
            overflow: 'hidden',
          }}
        >
          <div
            ref={scrollRef}
            style={{
              height: '380px',
              overflowY: 'auto',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(124,252,0,0.2) transparent',
            }}
          >
            {sorted.length === 0 && (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: 0.35,
                }}
              >
                <MessageCircle size={28} style={{ color: '#7CFC00' }} />
                <p
                  style={{
                    fontFamily: "'Bangers', cursive",
                    letterSpacing: '0.08em',
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '1rem',
                  }}
                >
                  Be the first to say something
                </p>
              </div>
            )}
            {sorted.map(msg => (
              <ChatBubble key={msg.id} msg={msg} isNew={newIds.has(msg.id)} />
            ))}
          </div>

          {/* Input or name prompt */}
          <div
            style={{
              borderTop: '3px solid #1a1a2a',
              padding: '12px',
              background: 'rgba(8,13,31,0.8)',
            }}
          >
            {!walletAddress ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                }}
              >
                <p
                  style={{
                    fontSize: '0.8rem',
                    color: 'rgba(255,255,255,0.35)',
                    fontFamily: 'monospace',
                  }}
                >
                  Connect wallet to chat
                </p>
                <button
                  onClick={() => login()}
                  onMouseEnter={playHover}
                  style={{
                    background: '#7CFC00',
                    color: '#0a0a0f',
                    border: '3px solid rgba(0,0,0,0.6)',
                    boxShadow: '3px 3px 0 rgba(0,0,0,0.7)',
                    padding: '7px 16px',
                    fontFamily: "'Bangers', cursive",
                    fontSize: '0.9rem',
                    letterSpacing: '0.1em',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'transform 0.1s ease',
                  }}
                  onMouseDown={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(1px)'; }}
                  onMouseUp={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'none'; }}
                >
                  Connect Wallet
                </button>
              </div>
            ) : showNamePrompt ? (
              <form onSubmit={handleNameSubmit} className="flex gap-2">
                <div className="flex-1">
                  <input
                    ref={inputRef}
                    autoFocus
                    type="text"
                    value={nameInput}
                    onChange={e => setNameInput(e.target.value)}
                    placeholder="Pick a display name..."
                    maxLength={24}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.06)',
                      border: '2px solid rgba(124,252,0,0.4)',
                      color: '#fff',
                      padding: '9px 12px',
                      outline: 'none',
                      fontSize: '0.9rem',
                      fontFamily: 'monospace',
                      boxShadow: '0 0 8px rgba(124,252,0,0.15)',
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!nameInput.trim()}
                  onMouseEnter={playHover}
                  style={{
                    background: '#7CFC00',
                    color: '#0a0a0f',
                    border: '3px solid rgba(0,0,0,0.6)',
                    boxShadow: '3px 3px 0 rgba(0,0,0,0.7)',
                    padding: '9px 18px',
                    fontFamily: "'Bangers', cursive",
                    fontSize: '0.95rem',
                    letterSpacing: '0.1em',
                    cursor: nameInput.trim() ? 'pointer' : 'not-allowed',
                    opacity: nameInput.trim() ? 1 : 0.5,
                    transition: 'transform 0.1s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Set Name
                </button>
              </form>
            ) : (
              <form onSubmit={handleSend} className="flex gap-2">
                <input
                  type="text"
                  value={messageInput}
                  onChange={e => setMessageInput(e.target.value)}
                  placeholder="Say something..."
                  maxLength={280}
                  disabled={sending}
                  style={{
                    flex: 1,
                    background: 'rgba(255,255,255,0.05)',
                    border: '2px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                    padding: '9px 12px',
                    outline: 'none',
                    fontSize: '0.9rem',
                    transition: 'border-color 0.2s ease',
                  }}
                  onFocus={e => {
                    (e.target as HTMLInputElement).style.borderColor = 'rgba(124,252,0,0.4)';
                  }}
                  onBlur={e => {
                    (e.target as HTMLInputElement).style.borderColor = 'rgba(255,255,255,0.1)';
                  }}
                />
                <button
                  type="submit"
                  disabled={sending || !messageInput.trim()}
                  style={{
                    background: sending || !messageInput.trim() ? 'rgba(124,252,0,0.2)' : '#7CFC00',
                    color: '#0a0a0f',
                    border: '3px solid rgba(0,0,0,0.5)',
                    boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
                    padding: '9px 14px',
                    cursor: sending || !messageInput.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.15s ease, transform 0.1s ease',
                  }}
                  onMouseEnter={e => {
                    playHover();
                    if (!sending && messageInput.trim()) {
                      (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'none';
                  }}
                >
                  <Send size={16} />
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Character count */}
        {messageInput.length > 200 && (
          <p
            style={{
              textAlign: 'right',
              fontSize: '0.7rem',
              color: messageInput.length > 260 ? '#E63946' : 'rgba(255,255,255,0.3)',
              marginTop: '4px',
            }}
          >
            {280 - messageInput.length} chars left
          </p>
        )}
      </div>

      <style>{`
        @keyframes chat-pop {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const ArtistRoomPage: React.FC = () => {
  const headerRef = useScrollAnimation<HTMLElement>(0.05);

  const [bio, setBio] = useState<ArtistBioResponse | null>(null);
  const [bioLoaded, setBioLoaded] = useState(false);
  const [wipPosts, setWipPosts] = useState<WipPostsResponse[]>([]);

  // Fast initial fetch for bio
  useEffect(() => {
    getManyArtistBio()
      .then(results => {
        setBio(results[0] ?? null);
        setBioLoaded(true);
      })
      .catch(() => setBioLoaded(true));
  }, []);

  // Fast initial fetch for WIP posts
  useEffect(() => {
    getManyWipPosts()
      .then(results => setWipPosts(results))
      .catch(() => {});
  }, []);

  // Live updates for bio
  const { data: bioEntries } = useRealtimeData<ArtistBioResponse[]>(
    subscribeManyArtistBio,
    true,
  );

  useEffect(() => {
    if (bioEntries !== undefined && bioEntries !== null) {
      setBio(bioEntries[0] ?? null);
      setBioLoaded(true);
    }
  }, [bioEntries]);

  // Live updates for WIP posts
  const { data: wipData } = useRealtimeData<WipPostsResponse[]>(subscribeManyWipPosts, true);

  useEffect(() => {
    if (wipData) setWipPosts(wipData);
  }, [wipData]);

  return (
    <PageLayout fullBleed footer={false}>
      {/* TV grain */}
      <div className="tv-grain" aria-hidden="true" />

      {/* Page header */}
      <header
        ref={headerRef}
        className="scroll-fade-up relative pt-12 pb-6 px-6 flex flex-col items-center"
      >
        {/* Back link */}
        <Link
          to="/"
          onClick={playWhoosh}
          onMouseEnter={e => {
            playHover();
            (e.currentTarget as HTMLAnchorElement).style.color = '#7CFC00';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.4)';
          }}
          className="absolute top-6 left-6 inline-flex items-center gap-2 text-xs font-bold uppercase transition-all duration-200"
          style={{
            fontFamily: "'Bangers', cursive",
            letterSpacing: '0.12em',
            color: 'rgba(255,255,255,0.4)',
            textDecoration: 'none',
          }}
        >
          <ArrowLeft size={14} />
          Back to Home
        </Link>

        {/* Title block */}
        <div
          style={{
            display: 'inline-block',
            background: '#E63946',
            color: '#fff',
            fontFamily: "'Bangers', cursive",
            fontSize: '0.8rem',
            letterSpacing: '0.22em',
            padding: '2px 14px',
            border: '3px solid rgba(0,0,0,0.7)',
            boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
            transform: 'rotate(-1.2deg)',
            marginBottom: '10px',
            userSelect: 'none',
          }}
        >
          EXCLUSIVE ACCESS
        </div>

        <h1
          style={{
            fontFamily: "'Bangers', cursive",
            fontSize: 'clamp(3rem, 12vw, 7rem)',
            letterSpacing: '0.08em',
            color: '#ffffff',
            textShadow:
              '4px 4px 0 rgba(0,0,0,0.9), 0 0 40px rgba(124,252,0,0.15), 6px 6px 0 #7CFC00',
            textAlign: 'center',
            lineHeight: 0.95,
          }}
        >
          ARTIST
          <br />
          <span style={{ color: '#7CFC00', textShadow: '4px 4px 0 rgba(0,0,0,0.9)' }}>
            ROOM
          </span>
        </h1>

        {/* Decorative line */}
        <div
          style={{
            width: '100%',
            maxWidth: '400px',
            height: '3px',
            background: 'linear-gradient(90deg, transparent, #E63946, #7CFC00, #E63946, transparent)',
            marginTop: '16px',
            opacity: 0.5,
          }}
        />
      </header>

      {/* Bio */}
      <BioSection bio={bio} loading={!bioLoaded} />

      {/* WIP Gallery */}
      <WipGallery posts={wipPosts} />

      {/* Fan Art */}
      <FanArtSection />

      {/* Chat */}
      <ChatSection />

      {/* Footer */}
      <footer
        className="py-8 px-6 flex justify-center"
        style={{ borderTop: '3px solid rgba(124,252,0,0.08)' }}
      >
        <Link
          to="/"
          onClick={playWhoosh}
          onMouseEnter={e => {
            playHover();
            (e.currentTarget as HTMLAnchorElement).style.color = '#7CFC00';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.3)';
          }}
          className="inline-flex items-center gap-2 text-xs uppercase transition-all duration-200"
          style={{
            fontFamily: "'Bangers', cursive",
            fontSize: '1rem',
            letterSpacing: '0.12em',
            color: 'rgba(255,255,255,0.3)',
            textDecoration: 'none',
          }}
        >
          <ArrowLeft size={14} />
          Back to Danger Dan!
        </Link>
      </footer>

      <style>{`
        .scroll-fade-up {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .scroll-fade-up.scroll-visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </PageLayout>
  );
};

export default ArtistRoomPage;
